import type { SupportAttachmentDTO, SupportAttachmentKind } from '../types/support';

export const SUPPORT_ATTACHMENT_MEDIA_DB_VERSION = 1;
export const SUPPORT_ATTACHMENT_MEDIA_DB_NAME = 'support-chat-media';

/** Не кэшируем слишком большие файлы (обычно длинные видео). */
export const MAX_CACHEABLE_ATTACHMENT_BYTES = 20 * 1024 * 1024;

const STORE_NAME = 'attachments';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface AttachmentMediaCacheRecord {
  cacheKey: string;
  attachmentId: string;
  userId: string | number;
  threadId: string;
  mimeType: string;
  size: number;
  kind: SupportAttachmentKind;
  cachedAt: string;
  blob: Blob;
}

const isBrowser = (): boolean =>
  typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';

const makeCacheKey = (userId: string | number, attachmentId: string): string =>
  `${userId}:${attachmentId}`;

let dbPromise: Promise<IDBDatabase> | null = null;

const openDb = (): Promise<IDBDatabase> => {
  if (!isBrowser()) {
    return Promise.reject(new Error('IndexedDB недоступен'));
  }

  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(
      SUPPORT_ATTACHMENT_MEDIA_DB_NAME,
      SUPPORT_ATTACHMENT_MEDIA_DB_VERSION,
    );

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'cacheKey' });
        store.createIndex('userId', 'userId', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      dbPromise = null;
      reject(request.error ?? new Error('Не удалось открыть IndexedDB'));
    };
  });

  return dbPromise;
};

const readRecord = async (
  userId: string | number,
  attachmentId: string,
): Promise<AttachmentMediaCacheRecord | null> => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(makeCacheKey(userId, attachmentId));
    req.onsuccess = () => resolve((req.result as AttachmentMediaCacheRecord | undefined) ?? null);
    req.onerror = () => reject(req.error);
  });
};

export const shouldCacheAttachment = (
  attachment: Pick<SupportAttachmentDTO, 'size'>,
  blobSize?: number,
): boolean => {
  const bytes = blobSize ?? attachment.size;
  if (bytes <= 0) return true;
  return bytes <= MAX_CACHEABLE_ATTACHMENT_BYTES;
};

export const getCachedAttachmentBlob = async (
  userId: string | number,
  attachmentId: string,
): Promise<Blob | null> => {
  if (!isBrowser()) return null;

  try {
    const record = await readRecord(userId, attachmentId);
    if (!record?.blob) return null;

    const age = Date.now() - new Date(record.cachedAt).getTime();
    if (Number.isNaN(age) || age > CACHE_TTL_MS) {
      void deleteCachedAttachment(userId, attachmentId);
      return null;
    }

    return record.blob;
  } catch {
    return null;
  }
};

export const putCachedAttachmentBlob = async (
  userId: string | number,
  threadId: string,
  attachment: SupportAttachmentDTO,
  blob: Blob,
): Promise<void> => {
  if (!isBrowser() || !shouldCacheAttachment(attachment, blob.size)) return;

  const record: AttachmentMediaCacheRecord = {
    cacheKey: makeCacheKey(userId, attachment.id),
    attachmentId: attachment.id,
    userId,
    threadId,
    mimeType: attachment.mimeType || blob.type || 'application/octet-stream',
    size: attachment.size || blob.size,
    kind: attachment.kind,
    cachedAt: new Date().toISOString(),
    blob,
  };

  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // переполнение IndexedDB — не блокируем UI
  }
};

export const deleteCachedAttachment = async (
  userId: string | number,
  attachmentId: string,
): Promise<void> => {
  if (!isBrowser()) return;

  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(makeCacheKey(userId, attachmentId));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // ignore
  }
};

/** Удаляет медиа, не входящие в whitelist последних 20 сообщений. */
export const pruneAttachmentMediaCache = async (
  userId: string | number,
  keepAttachmentIds: Set<string>,
): Promise<void> => {
  if (!isBrowser()) return;

  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('userId');
      const request = index.openCursor(IDBKeyRange.only(userId));

      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return;
        const record = cursor.value as AttachmentMediaCacheRecord;
        if (!keepAttachmentIds.has(record.attachmentId)) {
          cursor.delete();
        }
        cursor.continue();
      };

      request.onerror = () => reject(request.error);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // ignore
  }
};

export const clearAttachmentMediaCache = async (userId?: string | number): Promise<void> => {
  if (!isBrowser()) return;

  try {
    const db = await openDb();

    if (userId == null) {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).clear();
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
      return;
    }

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('userId');
      const request = index.openCursor(IDBKeyRange.only(userId));

      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return;
        cursor.delete();
        cursor.continue();
      };

      request.onerror = () => reject(request.error);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // ignore
  }
};

export const hasCachedAttachment = async (
  userId: string | number,
  attachmentId: string,
): Promise<boolean> => {
  const blob = await getCachedAttachmentBlob(userId, attachmentId);
  return blob != null;
};
